import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { toast } from 'react-toastify';
import { aaiApi } from '../../services/aaiApi';
import { usePermissions } from '../../hooks/usePermissions';
import type {
  ActivityEvent, AiControl, ControlConnection, ControlEvent, IncidentItem, PostureItem,
} from '../../types/aai';
import { Action, Chip, Drawer, Kicker, RouteMark, Select } from '../../components/aa';
import type { ChipTone } from '../../components/aa';
import { relativeTime } from '../partner/format';

/*
 * The Controls drawers.
 *
 * Each opens one control object without losing the page, states what it is,
 * and offers the one action the Owner really has: recording a request or a
 * review. A recorded control event changes no access, route or release by
 * itself. The responsible owner acts on it, Activity shows it was asked for,
 * and the drawer flips to its completed state so the person sees the record.
 */

export const Tiles: React.FC<{ facts: { label: string; value: React.ReactNode }[]; cols?: 1 | 2 }> = ({ facts, cols = 2 }) => (
  <div className={`grid gap-3 ${cols === 2 ? 'sm:grid-cols-2' : ''}`}>
    {facts.map((f) => (
      <div key={f.label} className="rounded-card border border-line bg-canvas px-4 py-3.5">
        <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">{f.label}</div>
        <div className="mt-1.5 text-[14px] font-semibold leading-[20px] text-ink">{f.value}</div>
      </div>
    ))}
  </div>
);

const Steps: React.FC<{ steps: { title: string; desc: string }[]; done: boolean }> = ({ steps, done }) => (
  <div className="divide-y divide-line-soft">
    {steps.map((s, i) => (
      <div key={s.title} className="flex items-start gap-3.5 py-3.5">
        <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-full font-sans text-[12px] font-semibold ${done ? 'bg-[#3F7A2F] text-white' : i === 0 ? 'bg-ink text-white' : 'bg-canvas-inset text-ink-muted'}`}>
          {done ? <Check size={16} strokeWidth={2.5} /> : i + 1}
        </span>
        <div className="min-w-0">
          <div className="text-[14px] font-semibold text-ink">{s.title}</div>
          <div className="text-[12.5px] leading-[20px] text-ink-dim">{s.desc}</div>
        </div>
      </div>
    ))}
  </div>
);

const LABEL = 'mb-1.5 block text-[13px] font-semibold text-ink';
const FIELD = 'w-full rounded-card border border-line-strong bg-white px-4 py-3 text-[13.5px] leading-[21px] text-ink outline-none placeholder:text-ink-soft focus:border-ink';

// Who a recorded request reached, resolved from the object and the action: the
// company's providers, routes and connections, plus the connection owner when
// there is one. Read from the event so the UI never guesses.
export const sentTo = (e: ControlEvent | null): string[] => {
  const raw = e?.payload?.sent_to;
  if (!Array.isArray(raw)) return [];
  return raw.filter((x): x is string => typeof x === 'string' && x.length > 0);
};

export const sentLine = (e: ControlEvent | null): string => {
  const to = sentTo(e);
  if (to.length === 0) return 'Recorded in Activity. Nobody is named as responsible for this yet, so no one was notified.';
  return `Sent to ${to.join(' and ')} and recorded in Activity.`;
};

const useRecord = (onRecorded: () => void) => {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<ControlEvent | null>(null);
  const run = async (body: { kind: string; subject: string; status: string; payload: Record<string, unknown> }, message: string) => {
    setBusy(true);
    try {
      const res = await aaiApi.recordControlEvent(body);
      setDone(res.data);
      toast.success(`${message} ${sentLine(res.data)}`);
      onRecorded();
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      toast.error(detail || 'That did not save.');
    } finally {
      setBusy(false);
    }
  };
  return { busy, done, run };
};

export const CONNECTION_STATE: Record<string, { label: string; tone: ChipTone }> = {
  connected: { label: 'Healthy', tone: 'ok' },
  pending: { label: 'Pending', tone: 'warn' },
  needs_reconnect: { label: 'Needs review', tone: 'warn' },
  error: { label: 'Needs review', tone: 'warn' },
  expired: { label: 'Access expired', tone: 'warn' },
  disconnected: { label: 'Not connected', tone: 'neutral' },
};

export const connectionState = (status: string): { label: string; tone: ChipTone } =>
  CONNECTION_STATE[status] || { label: status, tone: 'neutral' };

export const providerLabel = (provider: string): string =>
  provider.split(/[-_]/).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const writeAccess = (scopes: string[]): string => {
  const writes = scopes.filter((s) => /write|manage|delete|send|admin/i.test(s));
  if (writes.length === 0) return 'None';
  return writes.join(', ');
};

/* ── Access review ─────────────────────────────────────────────────────── */

const ACCESS_STEPS = [
  { title: 'Owner reviews exact scope', desc: 'No access changes before approval.' },
  { title: 'Access record is versioned', desc: 'Earlier authorization remains visible.' },
  { title: 'Release gate rechecks freshness', desc: 'Dependent work stays blocked until the check passes.' },
];

export const AccessReviewDrawer: React.FC<{ connection: ControlConnection; onClose: () => void; onRecorded: () => void }> = ({ connection, onClose, onRecorded }) => {
  const { busy, done, run } = useRecord(onRecorded);
  const [duration, setDuration] = useState('90');
  const label = providerLabel(connection.provider);
  const owner = connection.owner_email || 'the connection owner';
  const state = connectionState(connection.status);

  const send = () => run({
    kind: 'access_review',
    subject: connection.provider,
    status: 'sent',
    payload: { owner_email: connection.owner_email, requested_duration_days: Number(duration), connection_status: connection.status },
  }, 'Access review request recorded.');

  return (
    <Drawer
      onClose={onClose}
      kicker={<Kicker>Access review</Kicker>}
      badge={done ? <Chip>Completed</Chip> : <Chip tone="warn">Needs owner</Chip>}
      title={`Ask ${owner} to renew ${label} access`}
      sub={done ? sentLine(done) : 'Review the exact scope, the dependent work and the owner before sending.'}
      footer={(
        <div className="flex flex-wrap items-center justify-end gap-3">
          {!done && <Action size="lg" variant="quiet" onClick={onClose}>Cancel</Action>}
          {!done && <Action size="lg" onClick={send} disabled={busy} reason="Sending">Send review request</Action>}
          {done && <Action size="lg" variant="quiet" onClick={onClose}>Close</Action>}
        </div>
      )}
    >
      <Tiles
        facts={[
          { label: 'Connection', value: `${label} · company connection` },
          { label: 'Affected work', value: 'Company work depending on this connection' },
          { label: 'Current state', value: state.label },
          { label: 'Write access', value: writeAccess(connection.scopes) },
        ]}
      />
      <div className="mt-6">
        <Kicker className="mb-2 border-b border-line-soft pb-2">What happens</Kicker>
        <Steps steps={ACCESS_STEPS} done={Boolean(done)} />
      </div>
      {!done && (
        <label className="mt-5 block">
          <span className={LABEL}>Requested duration</span>
          <Select value={duration} onChange={(e) => setDuration(e.target.value)}>
            <option value="30">30 days</option>
            <option value="90">90 days</option>
            <option value="180">180 days</option>
          </Select>
        </label>
      )}
    </Drawer>
  );
};

/* ── Security posture ──────────────────────────────────────────────────── */

export const POSTURE_STATE: Record<string, { label: string; tone: ChipTone }> = {
  verified: { label: 'Verified', tone: 'ok' },
  policy_ready: { label: 'Policy ready', tone: 'neutral' },
  verification_due: { label: 'Verification due', tone: 'warn' },
};

export const PostureDrawer: React.FC<{ item: PostureItem; onClose: () => void }> = ({ item, onClose }) => {
  const state = POSTURE_STATE[item.state];
  return (
    <Drawer
      onClose={onClose}
      kicker={<Kicker>Security posture</Kicker>}
      badge={<Chip tone={state.tone}>{state.label}</Chip>}
      title={item.title}
      sub={item.desc}
      footer={<div className="flex justify-end"><Action size="lg" variant="quiet" onClick={onClose}>Close</Action></div>}
    >
      <Tiles
        facts={[
          { label: 'Control state', value: state.label },
          { label: 'Evidence', value: item.evidence },
          { label: 'Owner', value: item.owner },
          { label: 'Fail-safe requirement', value: item.fail_safe },
        ]}
      />
      <p className="m-0 mt-5 text-[12.5px] leading-[20px] text-ink-dim">
        {item.last_verified_at ? `Last verified ${relativeTime(item.last_verified_at)}.` : 'No verification has been recorded for this control yet.'} Verification is recorded by Agents Anywhere with runtime evidence when they operate the platform for you; you read the state here.
      </p>
    </Drawer>
  );
};

/* ── Incident drill ────────────────────────────────────────────────────── */

export const IncidentDrillDrawer: React.FC<{ item: IncidentItem; owners: string[]; onClose: () => void; onRecorded: () => void }> = ({ item, owners, onClose, onRecorded }) => {
  const { busy, done, run } = useRecord(onRecorded);
  const scenarios = item.scenarios || [];
  const ownerOptions = owners.length > 0 ? owners : ['Company Owner'];
  const [scenario, setScenario] = useState(scenarios[0] || '');
  const [owner, setOwner] = useState(ownerOptions[0]);

  const start = () => run({
    kind: 'incident_drill', subject: item.id, status: 'started', payload: { scenario, owner },
  }, 'AI incident drill recorded.');

  return (
    <Drawer
      onClose={onClose}
      kicker={<Kicker>Incident control</Kicker>}
      badge={done ? <Chip>Completed</Chip> : <Chip>Runbook ready</Chip>}
      title="Run an AI incident drill"
      sub={done ? sentLine(done) : 'Rehearse containment without interrupting production.'}
      footer={(
        <div className="flex flex-wrap items-center justify-end gap-3">
          {!done && <Action size="lg" variant="quiet" onClick={onClose}>Cancel</Action>}
          {!done && <Action size="lg" onClick={start} disabled={busy} reason="Starting">Start tabletop drill</Action>}
          {done && <Action size="lg" variant="quiet" onClick={onClose}>Close</Action>}
        </div>
      )}
    >
      <Tiles
        facts={[
          { label: 'Covers', value: item.covers || item.desc },
          { label: 'Evidence required', value: item.evidence_required || 'Actors, decisions, tool calls and route' },
          { label: 'Business content', value: item.business_content || 'Restricted unless separately approved' },
        ]}
      />
      {!done && (
        <>
          <label className="mt-5 block">
            <span className={LABEL}>Scenario</span>
            <Select value={scenario} onChange={(e) => setScenario(e.target.value)}>
              {scenarios.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </label>
          <label className="mt-4 block">
            <span className={LABEL}>Incident owner</span>
            <Select value={owner} onChange={(e) => setOwner(e.target.value)}>
              {ownerOptions.map((o) => <option key={o} value={o}>{o}</option>)}
            </Select>
          </label>
        </>
      )}
      {done && (
        <p className="m-0 mt-5 text-[12.5px] leading-[20px] text-ink-dim">
          Recorded for {String(done.payload.owner || owner)}: {String(done.payload.scenario || scenario)}. The drill is a tabletop rehearsal; production routing is unchanged.
        </p>
      )}
    </Drawer>
  );
};

/* ── Emergency stop review ─────────────────────────────────────────────── */

export const EmergencyReviewDrawer: React.FC<{ item: IncidentItem; onClose: () => void; onRecorded: () => void }> = ({ item, onClose, onRecorded }) => {
  const { busy, done, run } = useRecord(onRecorded);
  const results = item.results || ['Controls remain current'];
  const [result, setResult] = useState(results[0]);
  const [note, setNote] = useState('');

  const record = () => {
    if (!note.trim()) {
      toast.error('Write the review note first.');
      return;
    }
    return run({ kind: 'emergency_review', subject: item.id, status: result, payload: { result, note: note.trim() } }, 'Emergency stop review recorded.');
  };

  return (
    <Drawer
      onClose={onClose}
      kicker={<Kicker>Emergency controls</Kicker>}
      badge={done ? <Chip>Completed</Chip> : <Chip tone="warn">Review due</Chip>}
      title="Review emergency stop boundaries"
      sub={done ? `Recorded: ${result}. ${sentLine(done)}` : 'Confirm the exact scope and recovery owner before an emergency action is ever needed.'}
      footer={(
        <div className="flex flex-wrap items-center justify-end gap-3">
          {!done && <Action size="lg" variant="quiet" onClick={onClose}>Cancel</Action>}
          {!done && <Action size="lg" onClick={record} disabled={busy} reason="Recording">Record review</Action>}
          {done && <Action size="lg" variant="quiet" onClick={onClose}>Close</Action>}
        </div>
      )}
    >
      <Tiles facts={(item.boundaries || []).map((b) => ({ label: b.label, value: b.value }))} />
      {!done && (
        <>
          <label className="mt-5 block">
            <span className={LABEL}>Review result</span>
            <Select value={result} onChange={(e) => setResult(e.target.value)}>
              {results.map((r) => <option key={r} value={r}>{r}</option>)}
            </Select>
          </label>
          <label className="mt-4 block">
            <span className={LABEL}>Review note</span>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={5} className={`${FIELD} resize-y`} placeholder="All stop paths have a named owner, audit event, and recovery checklist." />
          </label>
        </>
      )}
      {done && <p className="m-0 mt-5 text-[13px] leading-[21px] text-ink">{note}</p>}
    </Drawer>
  );
};

/* ── Connection access ─────────────────────────────────────────────────── */

type ConnectionMode = 'read' | 'scope' | 'disconnect';

// The tool whose permissions should open once the settings modal mounts its
// Connectors panel. Held here because the drawer that asks for it also
// opens from Controls, outside the modal, where no prop can reach the panel.
let pendingPermissionsTool: string | null = null;

export const takePendingPermissionsTool = (): string | null => {
  const tool = pendingPermissionsTool;
  pendingPermissionsTool = null;
  return tool;
};

export const ConnectionAccessDrawer: React.FC<{
  connection: ControlConnection;
  onClose: () => void;
  onRecorded: () => void;
  onOpenPermissions?: (provider: string) => void;
}> = ({ connection, onClose, onRecorded, onOpenPermissions }) => {
  const { busy, done, run } = useRecord(onRecorded);
  const { can } = usePermissions();
  const [mode, setMode] = useState<ConnectionMode>('read');
  const [note, setNote] = useState('');
  const [revoking, setRevoking] = useState(false);
  const label = providerLabel(connection.provider);
  const state = connectionState(connection.status);
  const canDisconnect = can('aai_manage_connectors');
  const connected = !['disconnected', 'revoked'].includes(connection.status);
  const me = (localStorage.getItem('user_email') || '').trim().toLowerCase();
  // Asking the grant holder for a change is how everybody else changes scope.
  // The holder asking themselves sent the request to nobody, so they change it.
  const holdsGrant = Boolean(me) && (connection.owner_email || '').trim().toLowerCase() === me;

  const openPermissions = () => {
    onClose();
    if (onOpenPermissions) {
      onOpenPermissions(connection.provider);
      return;
    }
    pendingPermissionsTool = connection.provider;
    window.dispatchEvent(new CustomEvent('settings:open', { detail: { section: 'connectors' } }));
  };

  const requestScopeChange = () => {
    if (!note.trim()) {
      toast.error('Say what should change in the scope.');
      return;
    }
    return run(
      { kind: 'scope_change_request', subject: connection.provider, status: 'sent', payload: { owner_email: connection.owner_email, note: note.trim() } },
      `Scope change requested from ${connection.owner_email || 'the connection owner'}.`,
    );
  };

  // Disconnecting a company connection is the Owner's own action: they hold
  // connector management, so this revokes now and records it, rather than
  // asking someone else to do what the Owner can do.
  const disconnect = async () => {
    setRevoking(true);
    try {
      await aaiApi.revokeConnector(connection.provider, 'organization');
      toast.success(`${label} disconnected from the company. Recorded in Activity.`);
      onRecorded();
      onClose();
    } catch {
      toast.error('Could not disconnect this connection.');
    } finally {
      setRevoking(false);
    }
  };

  const pending = (e: ControlEvent | null): React.ReactNode => {
    if (!e) return null;
    return <Chip tone="warn">Requested {relativeTime(e.created_at)}</Chip>;
  };

  return (
    <Drawer
      onClose={onClose}
      kicker={(
        <span className="flex items-center gap-3">
          <RouteMark kind="connection" glyph={label.charAt(0)} size="lg" className="!bg-ink !text-white" />
          <Kicker>Company connection</Kicker>
        </span>
      )}
      badge={<Chip tone={state.tone} dot>{state.label}</Chip>}
      title={label}
      sub={connection.account_name ? `Connected as ${connection.account_name}` : 'Connection and access policy metadata'}
      footer={(
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {pending(connection.scope_change_request)}
            {pending(connection.disconnect_request)}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {mode === 'read' && !done && (
              <>
                {holdsGrant && <Action size="lg" onClick={openPermissions}>Change permissions</Action>}
                {!holdsGrant && <Action size="lg" onClick={() => setMode('scope')}>Request scope change</Action>}
                <Action
                  size="lg"
                  variant="quiet"
                  className="!text-danger"
                  disabled={!canDisconnect || !connected}
                  reason={connected ? 'Disconnecting needs connector management for this company' : 'This connection is already disconnected'}
                  onClick={() => setMode('disconnect')}
                >
                  Disconnect from the company
                </Action>
              </>
            )}
            {mode === 'scope' && !done && (
              <>
                <Action size="lg" variant="ghost" onClick={() => setMode('read')} disabled={busy}>Back</Action>
                <Action size="lg" onClick={requestScopeChange} disabled={busy} reason="Sending">Send scope change request</Action>
              </>
            )}
            {mode === 'disconnect' && !done && (
              <>
                <Action size="lg" variant="ghost" onClick={() => setMode('read')} disabled={busy}>Back</Action>
                <Action size="lg" variant="danger" onClick={disconnect} disabled={revoking} reason="Disconnecting">Disconnect now</Action>
              </>
            )}
            {done && <Action size="lg" variant="quiet" onClick={onClose}>Close</Action>}
          </div>
        </div>
      )}
    >
      <div className="rounded-card bg-canvas-inset px-4 py-3.5">
        <div className="text-[12px] text-ink-dim">Source details</div>
        <div className="text-[14px] font-semibold text-ink">Connection and access policy metadata</div>
        <div className="mt-0.5 text-[12.5px] text-ink-dim">
          Shared with the company by its owner{connection.connected_at ? ` · connected ${relativeTime(connection.connected_at)}` : ''}{connection.access_review ? ` · review requested ${relativeTime(connection.access_review.created_at)}` : ''}
        </div>
      </div>

      <div className="mt-4">
        <Tiles
          facts={[
            { label: 'Owner', value: connection.owner_email || 'Not recorded' },
            { label: 'Credential', value: 'Company connection · token held by the platform, never by work' },
            { label: 'Authorized through', value: 'Company connection shared by its owner' },
            { label: 'Grant ends', value: 'Not recorded' },
            { label: 'Data class', value: 'Not recorded' },
            { label: 'Environment', value: 'Production' },
            { label: 'Last access review', value: connection.access_review ? `${relativeTime(connection.access_review.created_at)} · requested` : 'None recorded' },
            { label: 'Freshness', value: connection.last_sync_at ? `Checked ${relativeTime(connection.last_sync_at)}` : 'Not checked yet' },
          ]}
        />
      </div>

      <div className="mt-4 rounded-card border border-line px-4 py-3.5">
        <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-ink-soft">Exact data scope</div>
        {connection.scopes.length === 0 && <div className="mt-1.5 text-[14px] font-semibold text-ink">Scope as granted at connection</div>}
        {connection.scopes.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {connection.scopes.map((s) => <Chip key={s}>{s}</Chip>)}
          </div>
        )}
        <div className="mt-1.5 text-[13px] text-ink-dim">Write access: {writeAccess(connection.scopes)}</div>
      </div>

      <div className="mt-4 rounded-card bg-canvas-inset px-4 py-3.5">
        <div className="text-[12px] text-ink-dim">How access is enforced</div>
        <div className="text-[14px] font-semibold text-ink">Source permissions stay with the source</div>
        <div className="mt-0.5 text-[12.5px] text-ink-dim">Seeing this connection is not permission to browse it. Agents Anywhere cannot change source files.</div>
      </div>

      {mode === 'scope' && !done && (
        <label className="mt-5 block">
          <span className={LABEL}>What should change</span>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className={`${FIELD} resize-y`} placeholder="Narrow to contracts and renewal dates only." />
        </label>
      )}
      {mode === 'disconnect' && !done && (
        <label className="mt-5 block">
          <span className={LABEL}>Why (optional)</span>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className={`${FIELD} resize-y`} placeholder="No work depends on it any more." />
        </label>
      )}
      {done && (
        <p className="m-0 mt-5 text-[12.5px] leading-[20px] text-ink-dim">
          {sentLine(done)} Nothing changes until they act.
        </p>
      )}
    </Drawer>
  );
};

/* ── Capability contract (an approved route) ──────────────────────────── */

export interface RouteRow { alias: string; lane: string; fallback: string; status: string }

export const RouteContractDrawer: React.FC<{ route: RouteRow; ai: AiControl; onClose: () => void }> = ({ route, ai, onClose }) => {
  const navigate = useNavigate();
  const candidate = ai.registry?.current_model || null;
  const linked = ai.use_case_models.filter((m) => m.model);
  return (
    <Drawer
      onClose={onClose}
      kicker={<Kicker>Capability contract</Kicker>}
      badge={<Chip tone="ok" dot>Approved route · {route.alias}</Chip>}
      title={route.lane}
      sub="Work asks for this outcome and its policy, not a vendor model. The route can improve without changing the work."
      footer={(
        <div className="flex flex-wrap items-center justify-end gap-3">
          <Action size="lg" variant="quiet" onClick={onClose}>Close</Action>
          <Action size="lg" onClick={() => { onClose(); navigate('/controls/ai?tab=evaluations'); }}>Open evaluation</Action>
        </div>
      )}
    >
      <Tiles
        facts={[
          { label: 'Candidate', value: candidate || 'None' },
          { label: 'Quality', value: 'Not measured yet' },
          { label: 'Speed', value: 'Not measured yet' },
          { label: 'Cost', value: 'Not measured yet' },
        ]}
      />
      <div className="mt-3">
        <Tiles cols={1} facts={[
          { label: 'Fallback', value: `${route.fallback} · last approved route retained` },
          { label: 'Non-negotiable release boundary', value: 'Citation omissions and unsupported certainty remain release blockers.' },
        ]} />
      </div>
      <div className="mt-6">
        <Kicker className="mb-2 border-b border-line-soft pb-2">Linked work in your boundary</Kicker>
        {linked.length === 0 && <p className="m-0 py-3 text-[13px] text-ink-dim">Approved work does not name a model yet. Work runs on the managed pool through this route.</p>}
        <div className="divide-y divide-line-soft">
          {linked.map((m, i) => (
            <div key={`${m.model}-${i}`} className="flex items-center justify-between gap-3 py-3">
              <span className="flex items-center gap-3"><RouteMark kind="quick_win" size="sm" /><span className="text-[14px] font-semibold text-ink">{m.model}</span></span>
              <span className="text-[12.5px] font-semibold text-ink-muted">{m.count} approved</span>
            </div>
          ))}
        </div>
      </div>
    </Drawer>
  );
};

/* ── Evaluation set (a company capability's reviewed runs) ────────────── */

export interface EvaluationRow { name: string; department: string | null; adoption: number; success_rate: number | null }

export const EvaluationDrawer: React.FC<{ row: EvaluationRow; onClose: () => void; onRecorded: () => void }> = ({ row, onClose, onRecorded }) => {
  const { busy, done, run } = useRecord(onRecorded);
  const rate = row.success_rate === null ? 'Not measured yet' : `${Math.round(row.success_rate * 100)}%`;
  const request = () => run({ kind: 'evaluation_request', subject: row.name, status: 'requested', payload: { department: row.department } }, 'Evaluation requested.');
  return (
    <Drawer
      onClose={onClose}
      kicker={<Kicker>Evaluation set</Kicker>}
      badge={done ? <Chip>Requested</Chip> : <Chip>Reviewed runs</Chip>}
      title={row.name}
      sub={done ? sentLine(done) : `${row.department || 'Whole company'} · evidence from runs people reviewed, private content excluded.`}
      footer={(
        <div className="flex flex-wrap items-center justify-end gap-3">
          <Action size="lg" variant="quiet" onClick={onClose}>Close</Action>
          {!done && <Action size="lg" onClick={request} disabled={busy} reason="Requesting">Request evaluation</Action>}
        </div>
      )}
    >
      <Tiles
        facts={[
          { label: 'Coverage', value: `${row.adoption} people using it` },
          { label: 'Candidate', value: rate },
          { label: 'Release floor', value: 'Not set' },
          { label: 'Run by', value: 'Agents Anywhere, on request' },
        ]}
      />
      <p className="m-0 mt-5 text-[12.5px] leading-[20px] text-ink-dim">
        A representative evaluation set is prepared and run by Agents Anywhere, who operate the managed pool. Requesting one notifies them and records it; production routing is unchanged.
      </p>
    </Drawer>
  );
};

/* ── Capability event ──────────────────────────────────────────────────── */

export interface HistoryItem { id: string; at: string | null; actor: string; title: string; desc: string; trust: string }

export const controlEventToHistory = (e: ControlEvent): HistoryItem => ({
  id: e.id,
  at: e.created_at,
  actor: e.actor_email || 'The company',
  title: `${e.subject.replace(/_/g, ' ')} · ${e.kind.replace(/_/g, ' ')}`,
  desc: `Recorded as ${e.status}.`,
  trust: 'record_only',
});

export const activityToHistory = (e: ActivityEvent): HistoryItem => ({
  id: e.id,
  at: e.created_at,
  actor: e.actor_email || 'The company',
  title: e.label,
  desc: e.entity_title ? `Concerns ${e.entity_title}.` : 'Operational record.',
  trust: e.trust,
});

export const CapabilityEventDrawer: React.FC<{ item: HistoryItem; onClose: () => void }> = ({ item, onClose }) => (
  <Drawer
    onClose={onClose}
    kicker={<Kicker>Capability event</Kicker>}
    badge={<Chip>{item.at ? relativeTime(item.at) : 'Undated'}</Chip>}
    title={item.title}
    sub={item.desc}
    footer={<div className="flex justify-end"><Action size="lg" variant="quiet" onClick={onClose}>Close</Action></div>}
  >
    <Tiles
      facts={[
        { label: 'Actor', value: item.actor },
        { label: 'Use', value: item.trust === 'trusted' ? 'Trusted' : 'Record only' },
      ]}
    />
    <div className="mt-3">
      <Tiles cols={1} facts={[{ label: 'Release control', value: 'Evidence and named approval required · previous approved route retained' }]} />
    </div>
  </Drawer>
);
